import React, { useState } from "react";
import { Alert } from "antd";
import {
  InfoCircleFilled,
  RightOutlined,
  CloseOutlined,
} from "@ant-design/icons";

interface Props {
  className?: string;
  tone?: "neutral" | "warning";
}

const TONE_STYLES = {
  // Neutral used to hardcode Tailwind's `primary` (#0075E3) — a flat
  // brand blue that never matches the accent tokens the glass theme
  // actually paints every other blue element with (--vi-accent: a
  // lighter #5b9df2 in dark mode, a deeper #1761c5 in light mode), so
  // this button always looked like a slightly different shade of blue
  // next to everything else on a themed page. Driven by `style` instead
  // of Tailwind classes since the accent is a CSS var, not a static hex
  // Tailwind can bake an opacity modifier onto.
  neutral: {
    type: "info" as const,
    className: "",
    style: {
      backgroundColor:
        "color-mix(in srgb, var(--vi-accent) 8%, transparent)",
      borderColor: "color-mix(in srgb, var(--vi-accent) 35%, transparent)",
    },
    textStyle: { color: "var(--vi-accent)" } as React.CSSProperties,
  },
  warning: {
    type: "warning" as const,
    className: "!bg-[#FDF6E9] !border-[#B07A1E]/30",
    style: undefined as React.CSSProperties | undefined,
    textStyle: { color: "#B07A1E" } as React.CSSProperties,
  },
};

const DISCLAIMER_TEXT = {
  title: "Disclaimer & Limitation of Liability",
  sections: [
    {
      heading: "1. No Investment Advice",
      body: `The Platform provides access to financial information, including stock quotes, market data, news, analysis, and other content ("Information") related to publicly traded companies and financial instruments. All Information provided on this Platform is for general informational and educational purposes only.

Nothing contained on this Platform constitutes a solicitation, recommendation, endorsement, or offer by us to buy or sell any securities, traded assets, or other financial instruments. You should not rely upon any Information on this Platform as financial, investment, legal, or tax advice. We strongly recommend that you consult with a qualified licensed professional before making any financial or trading decisions.`,
    },
    {
      heading: "2. Accuracy of Information",
      body: `While we strive to provide accurate, reliable, and up-to-date Information, the data is provided on an "as-is" and "as-available" basis. We make no representations or warranties of any kind, express or implied, regarding the accuracy, completeness, timeliness, suitability, or availability of the Information. Any reliance you place on such Information is strictly at your own risk.`,
    },
    {
      heading: "3. Limitation of Liability",
      body: `UNDER NO CIRCUMSTANCES SHALL WE BE LIABLE FOR ANY DIRECT, INDIRECT, INCIDENTAL, SPECIAL, CONSEQUENTIAL, OR PUNITIVE DAMAGES ARISING OUT OF OR RELATED TO YOUR USE OF THE PLATFORM OR THE INFORMATION.

This includes, but is not limited to, lost profits, trading losses, investment failures, data loss, damage to property, or personal injury, even if we have been advised of the possibility of such damages. You assume full responsibility for all trading and investment decisions you make.`,
    },
  ],
};

const DisclaimerButton: React.FC<Props> = ({
  className = "",
  tone = "neutral",
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const styles = TONE_STYLES[tone];

  return (
    <>
      <Alert
        type={styles.type}
        showIcon
        icon={<InfoCircleFilled style={styles.textStyle} />}
        onClick={() => setIsOpen(true)}
        className={`!rounded-xl !py-2.5 !px-4 cursor-pointer transition-shadow hover:shadow-sm ${styles.className} ${className}`}
        style={styles.style}
        message={
          <div className="flex items-center justify-between gap-3">
            <span
              className="flex items-center gap-1 text-[11px] font-semibold whitespace-nowrap"
              style={styles.textStyle}
            >
              Read disclaimer <RightOutlined style={{ fontSize: 9 }} />
            </span>
          </div>
        }
      />

      {isOpen && (
        <div
          className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4"
          onClick={() => setIsOpen(false)}
        >
          {/* This modal used to hardcode a light-mode palette (bg-white,
              #23312E/#5C6B66 text) that only looked right because some
              pages' `.bg-white` overrides happened to darken the card
              behind it without also fixing its now-illegible hardcoded
              text colors. Styled off the shared --vi-* / --vi-overlay-*
              tokens directly instead, so it reads correctly in both
              modes on every page that mounts this component, with no
              dependency on a host page's own theme CSS. */}
          <div
            className="rounded-2xl max-w-lg w-full max-h-[80vh] flex flex-col overflow-hidden border"
            style={{
              background: "var(--vi-overlay-bg)",
              borderColor: "var(--vi-overlay-border)",
              boxShadow: "var(--vi-overlay-shadow)",
              backdropFilter: "var(--vi-overlay-blur)",
              WebkitBackdropFilter: "var(--vi-overlay-blur)",
            }}
            onClick={(e) => e.stopPropagation()}
          >
            {/* Fixed header */}
            <div
              className="flex items-center justify-between px-6 pt-6 pb-4 border-b flex-shrink-0"
              style={{ borderColor: "var(--vi-card-border)" }}
            >
              <h3
                className="font-serif text-[18px] font-semibold m-0"
                style={{ color: "var(--vi-text)" }}
              >
                {DISCLAIMER_TEXT.title}
              </h3>
              <button
                type="button"
                onClick={() => setIsOpen(false)}
                className="transition-colors"
                style={{ color: "var(--vi-text-secondary)" }}
                onMouseEnter={(e) =>
                  (e.currentTarget.style.color = "var(--vi-text)")
                }
                onMouseLeave={(e) =>
                  (e.currentTarget.style.color = "var(--vi-text-secondary)")
                }
                aria-label="Close disclaimer"
              >
                <CloseOutlined style={{ fontSize: 16 }} />
              </button>
            </div>

            {/* Scrollable body */}
            <div className="overflow-y-auto px-6 py-4">
              <div className="space-y-4">
                {DISCLAIMER_TEXT.sections.map((section, i) => (
                  <div key={i}>
                    <p
                      className="font-semibold text-[13.5px] m-0 mb-1"
                      style={{ color: "var(--vi-text)" }}
                    >
                      {section.heading}
                    </p>
                    <p
                      className="text-[13px] m-0 whitespace-pre-line leading-relaxed"
                      style={{ color: "var(--vi-text-secondary)" }}
                    >
                      {section.body}
                    </p>
                  </div>
                ))}
              </div>
            </div>

            {/* Fixed footer */}
            <div className="px-6 pb-6 pt-2 flex-shrink-0">
              <button
                type="button"
                onClick={() => setIsOpen(false)}
                className="w-full text-[13.5px] font-semibold rounded-xl py-2.5 hover:opacity-90 transition-opacity"
                style={{
                  background: "var(--vi-accent-grad)",
                  color: "var(--vi-accent-on)",
                }}
              >
                Close
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default DisclaimerButton;
